import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { projectsApi } from '@/lib/api/projects';

/** Acceptance of a governed milestone: the acceptor links at least one workspace file as evidence. */
export function MilestoneAcceptanceDialog({
  projectId,
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (evidenceFileIds: string[]) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);
  const { data: files = [], isLoading } = useQuery({
    queryKey: ['projectWorkspaceFiles', projectId],
    queryFn: () => projectsApi.listWorkspaceFiles(projectId),
    enabled: open,
  });

  const toggle = (id: string, checked: boolean) =>
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setSelected([]);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('workspace.milestones.acceptance.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('workspace.milestones.acceptance.dialogDescription')}</DialogDescription>
        </DialogHeader>
        {isLoading ? null : files.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('workspace.milestones.acceptance.noFiles')}{' '}
            <Link to={`/projects/${encodeURIComponent(projectId)}/workspace/documents`} className="underline">
              {t('workspace.milestones.acceptance.openFiles')}
            </Link>
          </p>
        ) : (
          <ul className="max-h-72 space-y-2 overflow-y-auto">
            {files.map((f) => (
              <li key={f.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  id={`evidence-${f.id}`}
                  checked={selected.includes(f.id)}
                  onCheckedChange={(checked) => toggle(f.id, checked === true)}
                />
                <label htmlFor={`evidence-${f.id}`} className="truncate">
                  {f.originalName}
                </label>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('workspace.milestones.acceptance.cancel')}
          </Button>
          <Button
            variant="success"
            onClick={() => onConfirm(selected)}
            disabled={isPending || selected.length === 0}
          >
            {t('workspace.milestones.acceptance.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
